import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { OptionsAutocomplete } from "src/app/core/interfaces/optionsAutocomplete";
import { Utils } from 'src/app/core/resources/utils';
import { Global } from 'src/app/core/resources/global';
import { StandardMapComponent } from 'src/app/shared/standard-map/standard-map.component';
import { MenuServiceService } from '../../menu-service/menu-service.service';
import { Router } from '@angular/router';
import { Fmt } from 'src/app/core/messages/fmt';
import { DateManager } from 'src/app/core/managers/date.manager';
import { AddressCargo } from 'src/app/core/interfaces/addressCargo';
import { DateFormatPipe } from 'src/app/core/pipe/dateFormat.pipe';
import { of, Subscription } from 'rxjs';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { Patterns } from 'src/app/core/resources/patterns';
import { Permission } from 'src/app/core/resources/permission';
import { PermissionRole } from 'src/app/core/resources/permission-role';
import { Cargo } from 'src/app/core/interfaces/cargo';
import { catchError, debounceTime, distinctUntilChanged, map, take } from 'rxjs/operators';
import { ServiceResources } from '../../resources/services';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { GuideService } from 'src/app/modules/guides/guide.service';
import { GeoValidationService } from 'src/app/core/services/geo-validation.service';
import { municipalityPolygonValidator } from 'src/app/core/validators/municipality-polygon.validator';

@Component({
  selector: 'app-origin-load-service-form',
  templateUrl: './origin-load-service-form.component.html',
  styleUrls: ['./origin-load-service-form.component.scss']
})

export class OriginLoadServiceFormComponent implements OnInit, OnDestroy {
  @ViewChild(StandardMapComponent) standardMap!: StandardMapComponent;
  permission = Permission;
  private mapPolygonSub: Subscription = new Subscription();
  private originMarkerKeys: string[] = [];
  showOriginMap: boolean = true;

  toggleOriginMap(): void {
    this.showOriginMap = !this.showOriginMap;
  }
  originControl = new FormControl('', Validators.required);
  initialAddresses: { initialValue: string, initialThirdParty: string }[] = [];

  readonly baseOriginCityOptions: OptionsAutocomplete = {
    title: 'Origen (Municipio o Ciudad)',
    appearance: 'outline',
    requireFullCity: true
  };
  originCityOptions: OptionsAutocomplete = {
    ...this.baseOriginCityOptions,
    isInternational: false,
    requireFullCity: true
  };

  readonly loadDateOptions = { min: new Date(), max: DateManager.add(new Date(), 30, 'days') };
  loadDateControl = new FormControl(null, Validators.required);
  isInternationalControl = new FormControl(false);

  originAddresses = new FormArray([]);
  originAddressOptions = {
    title: 'Dirección de origen',
    appearance: 'outline',
    required: true
  };
  formValidate = '';

  readonly reteicaError = 'La ciudad de origen no tiene asignado un ReteIca por favor asocielo o cambie de ciudad';
  formSubscription: Subscription = new Subscription();
  originThirdPartyOptions: OptionsAutocomplete = {
    title: 'Remitente',
    appearance: 'outline',
  };
  isDialog: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public paramsDialog: { cargo: Cargo },
    public dialogRef: MatDialogRef<OriginLoadServiceFormComponent>,
    public utils: Utils,
    public menuService: MenuServiceService,
    private fb: FormBuilder,
    private router: Router,
    private snackBarService: SnackBarService,
    private patterns: Patterns,
    private permissionRole: PermissionRole,
    private serviceResources: ServiceResources,
    private guideService: GuideService,
    private geoValidation: GeoValidationService
  ) { }

  /**
* @description Executes the "initForm" method
*/
  async ngOnInit() {
    await this.checkDialogParams();
    this.setLocalValues();
    this.setSubscriptions();
  }

  private async checkDialogParams() {
    if (this.paramsDialog && this.paramsDialog.cargo) {
      this.isDialog = true;
      this.menuService.cargo = this.paramsDialog.cargo;
      this.menuService.patchForm(this.paramsDialog.cargo, false);
      await this.menuService.setCargoConsignments();
      await this.menuService.setCargoVehicle();
    }
  }

  private setLocalValues() {
    this.initialAddresses = [];
    const originCountryCode = this.utils.getNestedValue(this.menuService.form.value, 'cargoFeature.uploadDownload.origin.country.code');
    const originCountryName = this.utils.getNestedValue(this.menuService.form.value, 'cargoFeature.uploadDownload.origin.country.name');
    const reteica = this.utils.getNestedValue(this.menuService.form.value, 'shippingCost.reteicaPercentage');
    this.isInternationalControl.setValue(originCountryCode !== 'CO');
    const originCity = this.utils.getNestedValue(this.menuService.form.value, 'cargoFeature.uploadDownload.origin.name');
    const originCityId = this.utils.getNestedValue(this.menuService.form.value, 'cargoFeature.uploadDownload.origin.municipalityCode');

    const transportCompany = this.utils.getNestedValue(this.menuService.form.value, 'transportCompany');
    this.originCityOptions = {
      ...this.baseOriginCityOptions,
      initialValue: originCity ? originCity : '',
      isInternational: !!this.isInternationalControl.value,
      // Reteica de la ciudad por la empresa de transporte elegida en el paso "Contratante".
      transportNit: transportCompany && transportCompany.id ? transportCompany.id : undefined
    };
    this.originControl.setValue({
      name: originCity ? originCity : '',
      id: originCityId ? originCityId : '',
      country: originCountryName ? originCountryName : 'Colombia',
      code: originCountryCode ? originCountryCode : 'CO',
      reteica: reteica
    });
    const loadDate = this.utils.getNestedValue(this.menuService.form.value, 'dateLoad');
    this.loadDateControl.setValue(DateManager.stringToDate(loadDate));

    this.originAddresses.clear();
    this.addAddressForm();
    const originAddresses = this.utils.getNestedValue(this.menuService.form.value, 'cargoFeature.uploadDownload.origin.addresses');
    originAddresses.forEach((address: AddressCargo, index: number) => {
      if (index) this.addAddressForm();
      this.initialAddresses.push({ initialValue: address && address.address ? address.address : '', initialThirdParty: address && address.thirdPartyConsignment && address.thirdPartyConsignment.documentNumber ? address.thirdPartyConsignment.documentNumber : '' });
      this.originAddresses.at(index).get('address').setValue({
        address: address && address.address ? address.address : '',
        location: {
          lat: address && address.location && address.location.lat ? address.location.lat : 0,
          lng: address && address.location && address.location.lng ? address.location.lng : 0
        }
      });

      this.originAddresses.at(index).get('time').setValue(address && address.time ? address.time : '00:01');
      this.originAddresses.at(index).get('cellphone').setValue(address && address.cellphone ? address.cellphone : '');
      const hasCompleteAddress = !!(address && address.address && address.location && address.location.lat && address.location.lng);
      const hasThirdParty = (this.utils.addressHasThirdParty(address) || !hasCompleteAddress) && this.menuService.isMinistryService() && !this.isInternationalControl.value;
      this.originAddresses.at(index).get('hasThirdParty').setValue(hasThirdParty);
      //Ya llegó a la dirección en el tracking
      if (address && (address.state || (address.durationTime && address.durationTime.startDate))) {
        this.originAddresses.at(index).get('address').disable();
        this.originAddresses.at(index).get('time').disable();
      }
    });
    //Ya llegó a alguna dirección en el tracking
    if (originAddresses.some(address => address.state || (address.durationTime && address.durationTime.startDate))) {
      this.originControl.disable();
      this.loadDateControl.disable();
      this.isInternationalControl.disable();
    }
    this.checkPermissionsToEdit();
    this.refreshAddressesGeoValidity();
    this.refreshOriginPolygon();
  }

  private setSubscriptions() {
    this.formSubscription = this.menuService.formObservable
      .pipe(distinctUntilChanged())
      .subscribe(() => {
        this.setLocalValues();
      });
    this.mapPolygonSub.add(
      this.originControl.valueChanges.pipe(
        map((v: any) => (v && v.id) ? v.id : ''),
        distinctUntilChanged()
      ).subscribe(() => {
        this.refreshAddressesGeoValidity();
        this.refreshOriginPolygon();
      })
    );
    this.mapPolygonSub.add(
      this.isInternationalControl.valueChanges.subscribe(() => {
        this.refreshAddressesGeoValidity();
        this.refreshOriginPolygon();
      })
    );
    this.mapPolygonSub.add(
      this.originAddresses.valueChanges.pipe(debounceTime(150)).subscribe(() => {
        this.refreshAddressesGeoValidity();
        this.refreshOriginMarkers();
      })
    );
  }

  private refreshAddressesGeoValidity(): void {
    this.originAddresses.controls.forEach(address => {
      const addrCtrl = address.get('address');
      const tpAddrCtrl = address.get('thirdPartyAddress');
      if (addrCtrl && addrCtrl.enabled) addrCtrl.updateValueAndValidity({ emitEvent: false });
      if (tpAddrCtrl && tpAddrCtrl.enabled) tpAddrCtrl.updateValueAndValidity({ emitEvent: false });
    });
  }

  private refreshOriginPolygon(): void {
    if (!this.standardMap) return;
    this.standardMap.removeMunicipalityPolygon('origin');
    if (this.isInternationalControl.value) return;
    const cityId = this.originControl.value && this.originControl.value.id;
    if (cityId) this.loadAndShowOriginPolygon(cityId);
    this.refreshOriginMarkers();
  }

  private refreshOriginMarkers(): void {
    if (!this.standardMap) return;
    this.originMarkerKeys.forEach(key => this.standardMap.removeMarker(key));
    this.originMarkerKeys = [];
    if (this.isInternationalControl.value) return;
    this.originAddresses.controls.forEach((address, index) => {
      const direct: any = address.get('address') && address.get('address').value;
      const tp: any = address.get('thirdPartyAddress') && address.get('thirdPartyAddress').value;
      const usingThirdParty = !!(address.get('hasThirdParty') && address.get('hasThirdParty').value);
      const source = usingThirdParty
        ? (tp && tp.address && tp.address.location ? tp.address.location : null)
        : (direct && direct.location ? direct.location : null);
      const lat = source && source.lat ? +source.lat : 0;
      const lng = source && source.lng ? +source.lng : 0;
      if (!lat || !lng) return;
      const key = `origin-addr-${index}`;
      this.standardMap.createMarker(key, lat, lng, `Origen ${index + 1}`, undefined, `${index + 1}`);
      this.originMarkerKeys.push(key);
    });
  }

  private loadAndShowOriginPolygon(cityId: string): void {
    this.geoValidation.getMunicipalityPolygon(cityId).pipe(
      take(1),
      catchError(() => of(null))
    ).subscribe(poly => {
      if (poly && poly.geometry && this.standardMap) {
        this.standardMap.renderMunicipalityPolygon(poly.geometry, {
          key: 'origin',
          color: '#2563eb',
          fillOpacity: 0.1,
          fit: true
        });
      }
    });
  }

  onMapReady(): void {
    this.refreshOriginPolygon();
  }

  private hasOriginValidating(): boolean {
    for (const address of this.originAddresses.controls) {
      const activeCtrl = this.getActiveAddressControl(address as FormGroup);
      if (activeCtrl && activeCtrl.pending) return true;
    }
    return false;
  }

  /**
   * Espera a que terminen las validaciones async (polígono de municipio) que
   * estén pendientes antes de leer su resultado. Se usa polling corto porque
   * refreshAddressesGeoValidity() dispara updateValueAndValidity con
   * { emitEvent: false }, por lo que statusChanges no emite de forma fiable al
   * resolverse el validador. El timeout evita que la UI quede bloqueada si el
   * backend no responde.
   */
  private waitForOriginValidation(timeoutMs: number = 5000, intervalMs: number = 50): Promise<void> {
    return new Promise<void>(resolve => {
      const start = Date.now();
      const check = () => {
        if (!this.hasOriginValidating() || Date.now() - start >= timeoutMs) return resolve();
        setTimeout(check, intervalMs);
      };
      check();
    });
  }

  private hasOriginGeoErrors(): boolean {
    for (const address of this.originAddresses.controls) {
      const activeCtrl = this.getActiveAddressControl(address as FormGroup);
      if (activeCtrl && activeCtrl.errors && activeCtrl.errors.outsideMunicipality) return true;
    }
    return false;
  }

  private getActiveAddressControl(addressGroup: FormGroup) {
    const usingThirdParty = this.isAddressActive(addressGroup, true);
    return usingThirdParty ? addressGroup.get('thirdPartyAddress') : addressGroup.get('address');
  }

  ngOnDestroy(): void {
    this.mapPolygonSub.unsubscribe();
    if (this.standardMap) this.standardMap.removeMunicipalityPolygon('origin');
  }

  private checkPermissionsToEdit() {
    if (!this.canEditOriginLoadService) {
      this.formValidate = 'disable';
      this.originControl.disable();
      this.isInternationalControl.disable();
      this.loadDateControl.disable();
      this.originAddresses.controls.forEach(address => {
        address.get('address').disable();
        address.get('time').disable();
        address.get('cellphone').disable();
        address.get('hasThirdParty').disable();
      });
    }
  }

  get canSeeOriginLoadService(): boolean {
    return this.permissionRole.hasPermission(
      this.permission.cargo.module,
      this.permission.cargo.showLoadDownloadModule
    );
  }

  get canEditOriginLoadService(): boolean {
    return this.canSeeOriginLoadService && this.permissionRole.hasPermission(
      this.permission.cargo.module,
      this.permission.cargo.editLoadDownloadModule
    );
  }

  onChangeInternational() {
    this.originControl.setValue('');
    this.originAddresses.controls.forEach(addressOrigin => {
      addressOrigin.get('address').setValue('');
    });
    this.originCityOptions = {
      ...this.baseOriginCityOptions,
      isInternational: !!this.isInternationalControl.value,
      initialValue: '',
      transportNit: this.originCityOptions.transportNit
    };
  }

  addAddressForm() {
    this.originAddresses.push(this.getAddressForm());
  }

  private getAddressForm(): FormGroup {
    const group = this.fb.group({
      address: ['', Validators.required],
      time: ['00:01', Validators.required],
      cellphone: ['', [Validators.minLength(10), Validators.pattern(this.patterns.ONLY_NUMBERS.source)]],
      hasThirdParty: [this.menuService.isMinistryService() && !this.isInternationalControl.value],
      thirdParty: ['', Validators.required],
      thirdPartyAddress: ['', Validators.required]
    });
    this.attachGeoValidators(group);
    return group;
  }

  private attachGeoValidators(addressGroup: FormGroup): void {
    const addressCtrl = addressGroup.get('address');
    const thirdPartyAddressCtrl = addressGroup.get('thirdPartyAddress');
    if (addressCtrl) {
      addressCtrl.setAsyncValidators(municipalityPolygonValidator(this.geoValidation, () => {
        if (this.isInternationalControl.value) return null;
        if (!this.isAddressActive(addressGroup, false)) return null;
        const cityId = this.originControl.value && this.originControl.value.id;
        const val: any = addressCtrl.value;
        const lat = val && val.location && val.location.lat;
        const lng = val && val.location && val.location.lng;
        if (!cityId || lat == null || lng == null) return null;
        if (+lat === 0 && +lng === 0) return null;
        return { cityId, lat: +lat, lng: +lng };
      }));
    }
    if (thirdPartyAddressCtrl) {
      thirdPartyAddressCtrl.setAsyncValidators(municipalityPolygonValidator(this.geoValidation, () => {
        if (this.isInternationalControl.value) return null;
        if (!this.isAddressActive(addressGroup, true)) return null;
        const cityId = this.originControl.value && this.originControl.value.id;
        const val: any = thirdPartyAddressCtrl.value;
        const lat = val && val.address && val.address.location && val.address.location.lat;
        const lng = val && val.address && val.address.location && val.address.location.lng;
        if (!cityId || lat == null || lng == null) return null;
        if (+lat === 0 && +lng === 0) return null;
        return { cityId, lat: +lat, lng: +lng };
      }));
    }
  }

  isAddressActive(addressGroup: FormGroup, forThirdParty: boolean): boolean {
    const hasTpCtrl = addressGroup.get('hasThirdParty');
    const hasTp = !!(hasTpCtrl && hasTpCtrl.value);
    const usingThirdParty = !this.menuService.isTransportService() || hasTp;
    return forThirdParty ? usingThirdParty : !usingThirdParty;
  }

  removeSenderAddress(index: number) {
    this.originAddresses.removeAt(index);
    if (this.initialAddresses.length > index) this.initialAddresses.splice(index, 1);
  }

  get showErrorReteica(): boolean {
    if (!this.menuService.isRequiredReteica()) return false;
    if (!this.originControl.enabled) return false;
    const originCity = this.originControl.value;
    const reteica = originCity && originCity.reteica
      ? originCity.reteica
      : originCity && originCity.mainCity && originCity.mainCity.reteica
        ? originCity.mainCity.reteica
        : 0;
    const countryCode = originCity ? originCity.code ? originCity.code : 'CO' : null;
    return countryCode === 'CO' && !reteica;
  }

  get isInternationalTrip(): boolean {
    return this.utils.getNestedValue(this.menuService.form.value, 'cargoModel.tripType.name') === 'Internacional';
  }

  backToPreviousStep() {
    this.router.navigate([Fmt.string(this.serviceResources.serviceRequestUrls.contractor, this.menuService.cargo.consecutive)]);
  }

  private isValidForm(): boolean {
    if (this.utils.errorMessagesCustomized(this.originControl, 'origen')) return false;
    if (this.utils.errorMessagesCustomized(this.loadDateControl, 'fecha de carga')) return false;
    for (let i = 0; i < this.originAddresses.controls.length; i++) {
      const address = this.originAddresses.controls[i];
      if (this.utils.errorMessagesCustomized(address.get('time'), `hora ${i + 1}`)) return false;
      if (this.utils.errorMessagesCustomized(address.get('cellphone'), `celular ${i + 1}`, 10)) return false;
      if (!this.menuService.isTransportService()) {
        if (this.utils.errorMessagesCustomized(address.get('thirdParty'), `remitente ${i + 1}`)) return false;
        if (this.utils.errorMessagesCustomized(address.get('thirdPartyAddress'), `direccion ${i + 1}`)) return false;
      }
      else if (this.utils.errorMessagesCustomized(address.get('address'), `dirección ${i + 1}`)) return false;
    }
    return true;
  }

  async saveAndContinue(omitValidations: boolean = false) {
    if (this.isDialog && !this.isValidForm()) return;

    if (!omitValidations) {
      if (this.showErrorReteica)
        return this.snackBarService.openSnackBar(this.reteicaError, undefined, 'error');
      const isValidEstimatedTime = this.isValidEstimatedTime();
      if (!isValidEstimatedTime.isValid)
        return this.snackBarService.openSnackBar(isValidEstimatedTime.error, undefined, 'alert');
      this.refreshAddressesGeoValidity();
      await this.waitForOriginValidation();
      if (this.hasOriginValidating())
        return this.snackBarService.openSnackBar('Espera mientras se validan las direcciones de origen.', undefined, 'alert');
      if (this.hasOriginGeoErrors())
        return this.snackBarService.openSnackBar('Una o más direcciones están fuera del municipio de origen. Corrige las direcciones para continuar.', undefined, 'error');
    }

    this.originControl.enabled && this.menuService.form.get('cargoFeature.uploadDownload.origin.name').setValue(
      this.originControl.value && this.originControl.value.name ? this.originControl.value.name : ''
    );
    this.originControl.enabled && this.menuService.form.get('cargoFeature.uploadDownload.origin.municipalityCode').setValue(
      this.originControl.value && this.originControl.value.id ? this.originControl.value.id : ''
    );
    this.originControl.enabled && this.menuService.form.get('cargoFeature.uploadDownload.origin.country.name').setValue(
      this.originControl.value && this.originControl.value.country ? this.originControl.value.country : 'Colombia'
    );
    this.originControl.enabled && this.menuService.form.get('cargoFeature.uploadDownload.origin.country.code').setValue(
      this.originControl.value && this.originControl.value.code ? this.originControl.value.code : 'CO'
    );

    this.originControl.enabled && this.menuService.form.get('cargoFeature.uploadDownload.origin.reteica').setValue(
      this.originControl.value && this.originControl.value.reteica ? this.originControl.value.reteica : 0
    );


    const originReteica = this.originControl.value && this.originControl.value.reteica
      ? this.originControl.value.reteica
      : this.originControl.value && this.originControl.value.mainCity && this.originControl.value.mainCity.reteica
        ? this.originControl.value.mainCity.reteica
        : 0;

    const hasTaxExceptions = this.menuService.hasTaxExceptions();
    this.originControl.enabled && this.menuService.form.get('shippingCost.reteicaPercentage').setValue(
      originReteica && !this.isInternationalControl.value && !hasTaxExceptions
        ? originReteica
        : 0
    );
    this.loadDateControl.enabled && this.menuService.form.get('dateLoad').setValue(DateManager.utcTransform(DateManager.setStartOfDay(this.loadDateControl.value)));
    this.originAddresses.controls.forEach((address: FormGroup, index: number) => {
      if (!this.menuService.form.get('cargoFeature.uploadDownload.origin.addresses').at(index))
        this.menuService.form.get('cargoFeature.uploadDownload.origin.addresses').push(this.menuService.createOriginAddressGroup());
      const formAddress = this.menuService.form.get('cargoFeature.uploadDownload.origin.addresses').at(index);
      const isAddressEnabled = address.get('address').enabled;
      const isTimeEnabled = address.get('time').enabled;
      const localAddress = address.value;
      if (isAddressEnabled) {
        if (!this.menuService.isTransportService() || (address.get('hasThirdParty').value && !this.isInternationalControl.value)) {
          formAddress.get('thirdPartyConsignment.documentNumber').setValue(localAddress && localAddress.thirdParty && localAddress.thirdParty.document ? localAddress.thirdParty.document : '');
          formAddress.get('thirdPartyConsignment.documentType').setValue(localAddress && localAddress.thirdParty && localAddress.thirdParty.documentType ? localAddress.thirdParty.documentType : '');
          formAddress.get('thirdPartyConsignment.name').setValue(localAddress && localAddress.thirdParty && localAddress.thirdParty.name ? localAddress.thirdParty.name : '');
          const documentThirdParty = this.utils.getNestedValue(localAddress, 'thirdParty.document');
          const documentThirdPartyAddress = this.utils.getNestedValue(localAddress, 'thirdPartyAddress.information.document');
          if (documentThirdParty && documentThirdPartyAddress && documentThirdParty === documentThirdPartyAddress) {
            const thirdPartyAddress = this.utils.getNestedValue(localAddress, 'thirdPartyAddress');
            formAddress.get('thirdPartyConsignment.address').setValue(thirdPartyAddress && thirdPartyAddress.address && thirdPartyAddress.address.address ? thirdPartyAddress.address.address : '');
            formAddress.get('thirdPartyConsignment.branchOffice').setValue(thirdPartyAddress && thirdPartyAddress.address && thirdPartyAddress.address.id ? thirdPartyAddress.address.id : '');
            formAddress.get('address').setValue(thirdPartyAddress && thirdPartyAddress.branchOfficeName ? thirdPartyAddress.branchOfficeName : thirdPartyAddress.address && thirdPartyAddress.address.address ? thirdPartyAddress.address.address : '');
            formAddress.get('location.lat').setValue(thirdPartyAddress && thirdPartyAddress.address && thirdPartyAddress.address.location && thirdPartyAddress.address.location.lat ? thirdPartyAddress.address.location.lat : 0);
            formAddress.get('location.lng').setValue(thirdPartyAddress && thirdPartyAddress.address && thirdPartyAddress.address.location && thirdPartyAddress.address.location.lng ? thirdPartyAddress.address.location.lng : 0);
          } else {
            formAddress.get('thirdPartyConsignment.address').setValue('');
            formAddress.get('thirdPartyConsignment.branchOffice').setValue('');
            formAddress.get('address').setValue('');
            formAddress.get('location.lat').setValue(0);
            formAddress.get('location.lng').setValue(0);
          }
        } else {
          formAddress.get('address').setValue(localAddress && localAddress.address && localAddress.address.address ? localAddress.address.address : '');
          formAddress.get('location.lat').setValue(localAddress && localAddress.address && localAddress.address.location && localAddress.address.location.lat ? localAddress.address.location.lat : 0);
          formAddress.get('location.lng').setValue(localAddress && localAddress.address && localAddress.address.location && localAddress.address.location.lng ? localAddress.address.location.lng : 0);
        }
      }
      formAddress.get('cellphone').setValue(localAddress && localAddress.cellphone ? localAddress.cellphone : '');
      if (isTimeEnabled) formAddress.get('time').setValue(localAddress && localAddress.time ? localAddress.time : '00:01');
      formAddress.get('timeType').setValue('');
    });
    const formOriginAddresses = this.menuService.form.get('cargoFeature.uploadDownload.origin.addresses') as FormArray;
    for (let i = formOriginAddresses.controls.length - 1; i >= 0; i--) {
      if (i >= this.originAddresses.controls.length)
        formOriginAddresses.removeAt(i);
    }
    const formValue: Cargo = this.menuService.form.value;
    if (formValue.dateLoad && formValue.cargoFeature.uploadDownload.origin.addresses.length > 1 && !omitValidations &&
      formValue.cargoFeature.uploadDownload.origin.addresses.every(address => !!(address.address && address.location.lat && address.location.lng && address.time))) {
      const checkTripTimes = await this.menuService.checkTripTimes(formValue, true, false);
      if (!checkTripTimes.valid)
        return this.snackBarService.openSnackBar(checkTripTimes.error, undefined, 'error');
    }

    const { distancy, estimatedTime } = await this.menuService.getRouteGoogleData(this.menuService.form.value);
    this.menuService.form.get('distancy').setValue(distancy);
    this.menuService.form.get('estimatedTime').setValue(estimatedTime);
    if (this.menuService.form.get('routePlanId')) this.menuService.form.get('routePlanId').setValue(null);
    if (this.menuService.form.get('itineraryId')) this.menuService.form.get('itineraryId').setValue(null);
    if (this.menuService.form.get('itineraryOverviewPolylines')) this.menuService.form.get('itineraryOverviewPolylines').setValue(null);
    const guideChages = this.checkGuideChange(this.menuService.cargo, this.menuService.form.value);
    const cargoId = this.menuService.cargo.id;
    this.menuService.updateService().then(async () => {
      const guidesIds = this.utils.cargoCreatedGuide(this.menuService.form.value);
      if (guidesIds.length && this.isDialog && Object.keys(guideChages).length) await this.updateGuides(guidesIds, guideChages, cargoId);
      this.snackBarService.openSnackBar("Servicio actualizado exitosamente");
      if (!this.isDialog) this.continueToNextStep();
      else this.dialogRef.close({ state: true });
    }).catch(() => this.snackBarService.openSnackBar('Ocurrió un error al actualizar el servicio', undefined, 'error'));
  }

  continueToNextStep() {
    this.router.navigate([Fmt.string(this.serviceResources.serviceRequestUrls.destination, this.menuService.cargo.consecutive)]);
  }

  private isValidEstimatedTime(): { isValid: boolean, error: string } {
    let currentTime: Date;
    for (let i = 0; i < this.originAddresses.controls.length; i++) {
      const address = this.originAddresses.controls[i] as FormGroup;
      const time = address.getRawValue().time;
      const timeDate = new Date(`1990-01-01T${time}`);
      if (currentTime && DateManager.isSameOrBefore(timeDate, currentTime, 'minutes'))
        return { isValid: false, error: `La hora de la dirección ${i + 1} es anterior o igual a la dirección ${i}` };
      currentTime = timeDate;
    }
    return { isValid: true, error: '' };
  }

  private checkGuideChange(previousCargo: Cargo, newCargo: Cargo) {
    let guideChanges = {};
    const fieldsToCheck = [{
      field: 'originName',
      cargoField: 'cargoFeature.uploadDownload.origin.name',
    },
    {
      field: 'originCity',
      cargoField: 'cargoFeature.uploadDownload.origin.municipalityCode',
    },
    {
      field: 'sender.address',
      cargoField: 'cargoFeature.uploadDownload.origin.addresses.0.address',
    }
    ]
    fieldsToCheck.forEach(field => {
      const previousValue = this.utils.getNestedValue(previousCargo, field.cargoField);
      const newValue = this.utils.getNestedValue(newCargo, field.cargoField);
      if (previousValue && newValue && previousValue !== newValue) {
        if (field.field.includes('.')) {
          const fieldParts = field.field.split('.');
          guideChanges[fieldParts[0]] = {};
          guideChanges[fieldParts[0]][fieldParts[1]] = newValue;
        } else {
          guideChanges[field.field] = newValue;
        }
      }
    });
    return guideChanges;
  }

  private async updateGuides(guidesIds: string[], guideChanges: any, cargoId: string) {
    const guides = guidesIds.map(guideId => {
      return {
        guideId: guideId,
        ...guideChanges
      }
    });
    try {
      await this.guideService.updateGuides(guides, cargoId).toPromise();
    } catch (error) {
      console.error(error);
      return this.snackBarService.openSnackBar('Ocurrió un error al actualizar las guías', undefined, 'error');
    }
    return true;
  }
};